// src/api/commands.js
import { useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useOrg } from '../org/OrgContext';

const ok = (p) => p.then(r => r.data);

// Map org to API prefix. Assets are top-level now, so no prefix.
const prefixFor = (org) =>
  (org === 'GFM')   ? '/gfm'  :
  (org === 'GPM')   ? '/gpm'  :
  (org === 'Fleet') ? '/fleet':
  (org === 'Assets')? ''      : '';

/**
 * Tries <prefix><path> first; if 404 and a prefix was used, retries <path>.
 * Lets us keep legacy endpoints around while moving to unified ones.
 */
function makeCaller(api, org) {
  const pfx = prefixFor(org);
  return async function call(method, path, body) {
    const first = pfx + path;
    try {
      const res = await api[method](first, body);
      return res.data;
    } catch (e) {
      if (e?.response?.status === 404 && pfx) {
        const res2 = await api[method](path, body);
        return res2.data;
      }
      throw e;
    }
  };
}

/* ---------------------------------------------
   Helpers to normalize vehicle check payloads
--------------------------------------------- */
const looksLike = (s = '') => String(s).trim().toLowerCase();

function findMileageKey(obj = {}) {
  const keys = Object.keys(obj);
  const hit = keys.find(k => {
    const l = looksLike(k);
    return l === 'mileage' || l === 'odometer' || l.includes('mileage') || l.includes('odometer');
  });
  return hit || null;
}

function findRegKey(obj = {}) {
  const keys = Object.keys(obj);
  const hit = keys.find(k => {
    const l = looksLike(k);
    return (
      l === 'reg' ||
      l === 'plate' ||
      l === 'numberplate' ||
      l === 'vehiclenumberplate' ||
      l === 'registration' ||
      l.includes('number plate') ||
      l.includes('registration')
    );
  });
  return hit || null;
}

export function makeCommands(api, org) {
  const call = makeCaller(api, org);

  const commands = {
    // ---------- Auth (no prefix) ----------
    auth: {
      login: (username, password) => ok(api.post('/auth/login', { username, password })),
      refresh: (refreshToken)      => ok(api.post('/auth/refresh', { refreshToken })),
      changePassword: (oldPassword, newPassword) =>
        ok(api.post('/auth/change-password', { oldPassword, newPassword })),
      listUsers: () => ok(api.get('/auth/users')),
      adminResetPassword: (username) => ok(api.post('/auth/admin/reset-password', { username })),
    },

    // ---------- Inventory & History (GFM uses sites; others ignore this block) ----------
    inventory: {
      get:     (site='main')             => call('get',  `/inventory/${site}`),
      replace: (site='main', items=[])   => call('post', `/inventory/${site}`, items),
      patch:   (site='main', payload={}) => call('patch', `/inventory/${site}`, payload),
    },
    gfmDeliveries: {
      list: () => api.get('/gfm/deliveries').then(r => r.data),
      create: (payload) => api.post('/gfm/deliveries', payload).then(r => r.data),
      markDelivered: (id, body) => api.put(`/gfm/deliveries/${id}/deliver`, body).then(r => r.data),
      update: (id, payload) => api.patch(`/gfm/deliveries/${id}`, payload).then(r => r.data),
      delete: (id) => api.delete(`/gfm/deliveries/${id}`).then(r => r.data),
    },
    gfmTeams: {
      list:   ()                         => call('get',  '/teams'),
      add:    (name)                     => call('post', '/teams', { name }),
      rename: (oldName, newName)         => call('put',  '/teams/rename', { oldName, newName }),
      delete: (name)                     => call('delete', `/teams/${encodeURIComponent(name)}`),
    },

    history: {
      list: () => call('get', '/history'),
    },

    // ---------- Assets (top-level) ----------
    assets: {
      list:          ()          => call('get',  '/assets'),
      replace:       (list=[])   => call('post', '/assets', Array.isArray(list) ? list : []),

      delete:        (id)        => call('delete', `/assets/${encodeURIComponent(id)}`),
      update:  (id, asset)       => call('put', `/assets/${encodeURIComponent(id)}`, asset),

      historyList:   ()          => call('get',  '/assets/history'),
      historyAppend: (entries)   =>
        call('post', '/assets/history', Array.isArray(entries) ? entries : [entries]),
    },

    // ---------- GPM helpers ----------
    teams: {
      list:   ()                         => api.get('/gpm/teams').then(r => r.data),
      add:    (name)                     => api.post('/gpm/teams', { name }).then(r => r.data),
      rename: (oldName, newName)         => api.put('/gpm/teams/rename', { oldName, newName }).then(r => r.data),
    },
    suppliers: {
      list: ()      => call('get',  '/suppliers'),
      add:  (name)  => call('post', '/suppliers', { name }),
    },
    services: {
      list:   ()                         => call('get',  '/services'),
      add:    (service)                  => call('post', '/services', { service }),
      rename: (oldName, newName)         => call('put',  '/services/rename', { oldName, newName }),
    },
    keys: {
      list:          ()          => call('get',  '/keys'),
      replace:       (list=[])   => call('post', '/keys', Array.isArray(list) ? list : []),
      historyList:   ()          => call('get',  '/keys/history'),
      historyAppend: (entries)   =>
        call('post', '/keys/history', Array.isArray(entries) ? entries : [entries]),
    },
    gpmInventory: {
      get:     ()          => call('get',  `/inventory`),
      replace: (items=[])  => call('post', `/inventory`, items),
      patch:   (payload={})=> call('patch', `/inventory`, payload),
    },
    deliveries: {
      list:   ()               => call('get',  '/deliveries'),
      create: (payload={})     => call('post', '/deliveries', payload),
      update: (id, payload={}) => call('put',  `/deliveries/${id}`, payload),
      delete: (id)             => call('delete', `/deliveries/${id}`),
    },

    // ---------- Tickets (top-level under /assets) ----------
    tickets: {
      list:   ()               => call('get',  '/assets/tickets'),
      create: (payload={})     => call('post', '/assets/tickets', payload),
      update: (id, payload={}) => call('patch', `/assets/tickets/${id}`, payload),
    },

    // =========================================================================
    //                                F L E E T (unified)
    // =========================================================================
    fleet: {
      vehicles: {
        list:    ()         => call('get',  '/vehicles'),
        replace: (list=[])  => call('post', '/vehicles', Array.isArray(list) ? list : []),
      },

      // Bookings: currently unused (stubs to avoid breaking imports)
      bookings: {
        list:   async () => [],
        create: async () => ({ ok: false }),
        update: async () => ({ ok: false }),
      },

      checks: {
        list: () => call('get', '/checks'),

        /**
         * submit({ type, date, data, photos, notes })
         * - Auto-extracts reg & odometer from `data`
         * - Posts the server-required shape
         */
        submit: async (payload = {}) => {
          const { type, date, data = {}, photos = [], notes } = payload;

          const regKey = findRegKey(data);
          const milKey = findMileageKey(data);
          const reg = regKey ? data[regKey] : undefined;
          const odometerRaw = milKey ? data[milKey] : undefined;
          const odometer = odometerRaw != null ? Number(odometerRaw) : undefined;

          if (!reg) throw new Error('Vehicle number plate is required.');
          if (!(odometer >= 0)) throw new Error('Mileage (odometer) is required and must be a number.');

          const body = {
            reg,
            date: date || new Date().toISOString().slice(0, 10),
            odometer,
            items: data,                         // keep whole form payload for audit
            notes: notes ?? data.Notes ?? '',
            photos,                              // [{ name, dataUri }]
            _formType: type || 'standard',       // optional: helps analysis later
          };
          return call('post', '/checks', body);
        },

        templates: {
          list:    ()        => call('get',  '/check-templates'),
          replace: (arr=[])  => call('post', '/check-templates', Array.isArray(arr) ? arr : []),
        },
      },

      uploads: {
        url: (rel='') => rel, // axios baseURL handles host; consumers can use as-is
      },
    },

    // =========================================================================
    //                        F L E E T (legacy – deprecated)
    // =========================================================================
    vans: {
      list:          ()          => call('get',  '/vans'),
      replace:       (list=[])   => call('post', '/vans', Array.isArray(list) ? list : []),
      historyList:   ()          => call('get',  '/van-history'),
      historyAppend: (entries)   =>
        call('post', '/van-history', Array.isArray(entries) ? entries : [entries]),
    },

    // Old vehicleChecks endpoints. Prefer `fleet.checks`.
    vehicleChecks: {
      getPlates:  ()              => call('get',  '/plates'),
      getEntries: (plate)         => call('get',  `/entries/${encodeURIComponent(plate)}`),

      submit: (payload) => {
        // delegate to the unified version if present
        if (commands.fleet?.checks?.submit) {
          return commands.fleet.checks.submit(payload);
        }
        return call('post', '/checks', payload);
      },
    },
  };

  return commands;
}

export function useCommands(){
  const { api } = useAuth();
  const { org } = useOrg();
  return useMemo(() => makeCommands(api, org), [api, org]);
}
